/* eslint-disable no-unused-vars -- build may report useRef/API_BASE from other context */
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Settings, Headphones, X, FileSpreadsheet, Workflow } from 'lucide-react';
import {
  Button,
  Input,
  RadioGroup,
  RadioGroupItem,
  Spinner,
} from '../ui';
import SetWorkflowModal from '../modals/SetWorkflowModal';

const TranscriberService = ({
  projectName,
  setProjectName,
  knpFile,
  setKnpFile,
  selectedFiles,
  setSelectedFiles,
  driveUrl,
  setDriveUrl,
  driveFileInfo,
  setDriveFileInfo,
  validatingDriveUrl,
  validationErrors,
  setValidationErrors,
  isProcessing,
  getRootProps,
  getInputProps,
  isDragActive,
  error,
  checkProjectNameExists,
  handleProcessFiles,
  setShowSettingsModal,
  user = null,
}) => {
  const isPmOrAdmin = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';
  const canCreateTasks = isPmOrAdmin;

  const [inputSource, setInputSource] = useState('upload'); // 'upload' | 'drive'
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);

  const colors = {
    border: 'border-sky-200 dark:border-sky-800',
    bgLight: 'bg-sky-50 dark:bg-sky-900/20',
    text: 'text-sky-600 dark:text-sky-400',
  };

  const onDropKnp = React.useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) setKnpFile(acceptedFiles[0]);
  }, [setKnpFile]);

  const {
    getRootProps: getKnpRootProps,
    getInputProps: getKnpInputProps,
    isDragActive: isKnpDragActive,
  } = useDropzone({
    onDrop: onDropKnp,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/csv': ['.csv'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const onRadioChange = (value) => {
    setInputSource(value);
    if (value === 'upload') {
      setDriveUrl('');
      setDriveFileInfo(null);
      setValidationErrors((prev) => ({ ...prev, fileOrUrl: null }));
    } else {
      setSelectedFiles([]);
      setValidationErrors((prev) => ({ ...prev, fileOrUrl: null }));
    }
  };

  const handleSubmit = () => {
    if (inputSource === 'upload' && !selectedFiles.length) {
      setValidationErrors((prev) => ({ ...prev, fileOrUrl: 'Please select a file to upload' }));
      return;
    }
    if (inputSource === 'drive' && !driveUrl.trim()) {
      setValidationErrors((prev) => ({ ...prev, fileOrUrl: 'Please enter a Google Drive URL' }));
      return;
    }
    handleProcessFiles(null, inputSource);
  };

  const isValid = projectName.trim() &&
    (inputSource === 'upload' ? selectedFiles.length > 0 : driveUrl.trim());

  if (!canCreateTasks) {
    return (
      <div key="asr-service-interface" className="space-y-4">
        <div className="flex items-center">
          <div>
            <h3 className="text-foreground text-xl font-semibold">ASR Service</h3>
            <p className="text-muted-foreground mt-1">Audio Speech Recognition & Transcription</p>
          </div>
        </div>
        <div className="border-border bg-card rounded-lg border p-8 text-center">
          <p className="text-foreground font-medium mb-2">Only admin and PM can create new tasks.</p>
          <p className="text-muted-foreground text-sm">You can view existing projects from the sidebar (Transcriber Projects).</p>
        </div>
      </div>
    );
  }

  return (
    <div key="asr-service-interface" className="space-y-4">
      <div className="flex items-center">
        <div>
          <h3 className="text-foreground text-xl font-semibold">ASR Service</h3>
          <p className="text-muted-foreground mt-1">Audio Speech Recognition & Transcription</p>
        </div>
      </div>

      <div className="border-border bg-card rounded-lg border p-6 shadow-subtle-sm md:p-8">
        <div className="flex items-center justify-end mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSettingsModal(true)}
            title="Settings"
          >
            <Settings className="h-5 w-5 text-muted-foreground" />
          </Button>
        </div>

        {/* Project Name */}
        <div className="mb-6">
          <label htmlFor="projectName" className="text-foreground mb-2 block text-sm font-medium">
            Project Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="projectName"
            placeholder="Enter project name (required)"
            value={projectName}
            onChange={(e) => {
              setProjectName(e.target.value);
              if (validationErrors.projectName) {
                setValidationErrors((prev) => ({ ...prev, projectName: null }));
              }
            }}
            className={validationErrors.projectName ? 'border-destructive focus:ring-destructive' : ''}
          />
          {validationErrors.projectName && (
            <p className="text-destructive mt-1 text-sm">{validationErrors.projectName}</p>
          )}
          {projectName.trim() && checkProjectNameExists(projectName.trim()) && (
            <p className="text-muted-foreground mt-1 text-sm">A project with this name already exists. A unique name will be created automatically.</p>
          )}
        </div>

        {/* Set Workflow */}
        {canCreateTasks && (
          <div className="mb-6">
            <Button
              variant="outlined"
              size="default"
              className="w-full justify-center gap-2"
              onClick={() => setWorkflowModalOpen(true)}
              title="Set workflow"
            >
              <Workflow className="h-4 w-4" /> Set Workflow
            </Button>
            <p className="text-muted-foreground mt-1 text-xs">Available in project list or project detail after creation</p>
            <SetWorkflowModal
              isOpen={workflowModalOpen}
              onClose={() => setWorkflowModalOpen(false)}
              projectId={null}
              projectType="transcription_generation"
              isLocked={false}
              onSaved={() => setWorkflowModalOpen(false)}
            />
          </div>
        )}

        {/* Dictionary (Optional) - dropzone */}
        <div className="mb-6">
          <label className="text-foreground mb-2 block text-sm font-medium">
            Dictionary <span className="text-muted-foreground text-xs">(Optional)</span>
          </label>
          <div
            {...getKnpRootProps()}
            className={`relative cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-all duration-200 ${
              isKnpDragActive
                ? `${colors.border} ${colors.bgLight} scale-[1.01]`
                : 'border-gray-200 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-900/50 hover:border-gray-300 dark:hover:border-neutral-700'
            } ${knpFile ? `${colors.bgLight} ${colors.border}` : ''}`}
          >
            <input {...getKnpInputProps()} id="knpFileUpload" />
            <div className="flex flex-col items-center">
              {knpFile ? (
                <div className={`flex animate-in fade-in zoom-in duration-300 items-center space-x-3 rounded-lg border bg-white px-4 py-2 shadow-sm dark:bg-neutral-950 ${colors.border}`}>
                  <div className={`rounded-md p-2 ${colors.bgLight}`}>
                    <FileSpreadsheet className={`h-5 w-5 ${colors.text}`} />
                  </div>
                  <span className="max-w-xs truncate text-sm font-medium text-gray-900 dark:text-neutral-100">
                    {knpFile.name}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="rounded-full [&>svg]:text-muted-foreground hover:[&>svg]:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      setKnpFile(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div
                    className={`mb-3 rounded-full p-3 transition-colors ${
                      isKnpDragActive ? colors.bgLight : 'bg-gray-100 dark:bg-neutral-800'
                    }`}
                  >
                    <FileSpreadsheet className={`h-6 w-6 ${isKnpDragActive ? colors.text : 'text-gray-400 dark:text-neutral-500'}`} />
                  </div>
                  <p className="mb-1 text-sm font-medium text-gray-900 dark:text-neutral-100">
                    {isKnpDragActive ? 'Drop Dictionary here' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-neutral-500">
                    .xlsx or .csv with phrases column
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Source: Upload File vs Google Drive (radio) */}
        <div className="mb-6">
          <label className="text-foreground mb-2 block text-sm font-medium">
            Source <span className="text-muted-foreground text-xs">(Choose one)</span>
          </label>
          <RadioGroup
            value={inputSource}
            onValueChange={onRadioChange}
            className="mt-2 flex flex-row gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="upload" id="sourceUpload" />
              <label htmlFor="sourceUpload" className="text-foreground cursor-pointer text-sm">
                Upload File
              </label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="drive" id="sourceDrive" />
              <label htmlFor="sourceDrive" className="text-foreground cursor-pointer text-sm">
                Google Drive
              </label>
            </div>
          </RadioGroup>
        </div>

        {/* Upload File content - same design as synopsis MP4 dropzone */}
        {inputSource === 'upload' && (
          <div className="mb-6">
            <label className="text-foreground mb-2 block text-sm font-medium">
              Upload File <span className="text-destructive">*</span>
            </label>
            <div
              {...getRootProps()}
              className={`relative cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-all duration-200 ${
                isDragActive
                  ? `${colors.border} ${colors.bgLight} scale-[1.01]`
                  : 'border-gray-200 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-900/50 hover:border-gray-300 dark:hover:border-neutral-700'
              } ${selectedFiles.length > 0 ? `${colors.bgLight} ${colors.border}` : ''}`}
            >
              <input {...getInputProps()} id="transcriberFileUpload" />
              <div className="flex flex-col items-center">
                {selectedFiles.length > 0 ? (
                  <div className={`flex animate-in fade-in zoom-in duration-300 items-center space-x-3 rounded-lg border bg-white px-4 py-2 shadow-sm dark:bg-neutral-950 ${colors.border}`}>
                    <div className={`rounded-md p-2 ${colors.bgLight}`}>
                      <Upload className={`h-5 w-5 ${colors.text}`} />
                    </div>
                    <span className="max-w-xs truncate text-sm font-medium text-gray-900 dark:text-neutral-100">
                      {selectedFiles[0].name}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="rounded-full [&>svg]:text-muted-foreground hover:[&>svg]:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFiles([]);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div
                      className={`mb-4 rounded-full p-4 transition-colors ${
                        isDragActive ? colors.bgLight : 'bg-gray-100 dark:bg-neutral-800'
                      }`}
                    >
                      <Upload className={`h-8 w-8 ${isDragActive ? colors.text : 'text-gray-400 dark:text-neutral-500'}`} />
                    </div>
                    <p className="mb-1 font-medium text-gray-900 dark:text-neutral-100">
                      {isDragActive ? 'Drop your file here' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-neutral-500">
                      One audio or video file up to 300MB (e.g. MP4, MOV, WEBM, M4A, MP3, WAV)
                    </p>
                  </>
                )}
              </div>
            </div>
            {error && <p className="text-destructive mt-2 text-sm">{error}</p>}
          </div>
        )}

        {/* Google Drive content */}
        {inputSource === 'drive' && (
          <div className="mb-6">
            <div className="border-border bg-muted/30 rounded-lg border p-6">
              <p className="text-foreground mb-4 text-sm font-medium">Enter a Google Drive URL</p>
              <Input
                type="text"
                placeholder="https://drive.google.com/file/d/..."
                value={driveUrl}
                onChange={(e) => {
                  setDriveUrl(e.target.value);
                  setDriveFileInfo(null);
                  if (validationErrors.fileOrUrl) setValidationErrors((prev) => ({ ...prev, fileOrUrl: null }));
                }}
                className={validationErrors.fileOrUrl ? 'border-destructive focus:ring-destructive' : ''}
              />
              {validatingDriveUrl && (
                <div className="mt-4 flex items-center gap-2">
                  <Spinner size="sm" className="text-primary" />
                  <span className="text-muted-foreground text-sm">Validating URL...</span>
                </div>
              )}
              {driveFileInfo && (driveFileInfo.name || driveFileInfo.title) && (
                <div className="border-border bg-card mt-4 rounded-lg border p-3 text-sm text-foreground">
                  {driveFileInfo.name || driveFileInfo.title}
                </div>
              )}
              {validationErrors.fileOrUrl && <p className="text-destructive mt-2 text-sm">{validationErrors.fileOrUrl}</p>}
            </div>
          </div>
        )}

        {/* Process Button */}
        <div className="mt-8 flex justify-center">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={!isValid || isProcessing}
            className="min-w-[12rem]"
          >
            {isProcessing ? (
              <>
                <Spinner size="md" className="mr-3 text-white" />
                Processing...
              </>
            ) : (
              <>
                <Headphones className="mr-2 h-5 w-5" />
                Process Files
              </>
            )}
          </Button>
        </div>
        <p className="text-muted-foreground mt-2 text-center text-sm">
          {!isValid ? 'Fill required fields to process' : 'Ready to process'}
        </p>
      </div>
    </div>
  );
};

export default TranscriberService;
